import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export default function SubscriptionTest() {
  const { subscriptionInfo, loading, startFreeTrial } = useSubscription();
  const { user } = useAuth();

  const handleStartTrial = async () => {
    try {
      const success = await startFreeTrial();
      if (success) {
        toast.success('Free trial started successfully!');
        // Reload after a short delay to ensure the subscription is updated
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error('Error starting trial:', error);
      toast.error(`Error starting trial: ${error.message}`);
    }
  };

  if (loading) {
    return <div>Loading subscription information...</div>;
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Subscription Test</h1>
      
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Current Subscription Status</CardTitle>
          <CardDescription>Debug information about your current subscription</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center">
              <span className="font-medium w-48">Tier:</span>
              <Badge variant="outline" className="ml-2">
                {subscriptionInfo.tier}
              </Badge>
            </div>
            
            <div className="flex items-center">
              <span className="font-medium w-48">Status:</span>
              <span className="ml-2">{subscriptionInfo.status}</span>
            </div>
            
            <div className="flex items-center">
              <span className="font-medium w-48">Trial Ends At:</span>
              <span className="ml-2">
                {subscriptionInfo.trialEndsAt 
                  ? new Date(subscriptionInfo.trialEndsAt).toLocaleString() 
                  : 'N/A'}
              </span>
            </div>
            
            <div className="flex items-center">
              <span className="font-medium w-48">Remaining Trial Days:</span>
              <span className="ml-2">{subscriptionInfo.remainingTrialDays}</span>
            </div>
            
            <div className="flex items-center">
              <span className="font-medium w-48">Is Trial Expired:</span>
              <span className="ml-2">
                {subscriptionInfo.isTrialExpired ? 'Yes' : 'No'}
              </span>
            </div>
            
            <div className="pt-4">
              <h3 className="font-medium mb-2">Feature Access:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {['dashboard_view', 'roi_tracker', 'founder_journal', 'idea_assessment', 'boss_mode', 'social_tracker', 'prompt_library'].map(feature => (
                  <div key={feature} className="flex items-center">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded mr-2">
                      {feature}
                    </span>
                    <span className="ml-auto">
                      {subscriptionInfo.canAccessFeature(feature) ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          Allowed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700">
                          Denied
                        </Badge>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="space-x-4">
        <Button 
          onClick={handleStartTrial}
          disabled={subscriptionInfo.tier === 'free_trial' || loading}
        >
          {subscriptionInfo.tier === 'free_trial' 
            ? 'Trial Active' 
            : loading ? 'Starting...' : 'Start 7-Day Free Trial'}
        </Button>
        
        <Button 
          variant="outline" 
          onClick={() => {
            console.log('Subscription Info:', subscriptionInfo);
            alert('Check browser console for subscription info');
          }}
        >
          Log Subscription Info
        </Button>
      </div>
      
      <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="font-medium text-yellow-800 mb-2">Debug Information</h3>
        <pre className="text-xs bg-white p-2 rounded overflow-auto">
          {JSON.stringify(subscriptionInfo, null, 2)}
        </pre>
      </div>
    </div>
  );
}
